/**
 * Input Component
 *
 * Form input component with label, validation states, and error messages.
 * Supports text, email, password, and textarea types.
 *
 * CRITICAL CONSTRAINTS:
 * - Tailwind CSS core utilities only (NO custom compilation)
 * - TypeScript strict mode (NO 'any' types)
 * - Professional focus states with ring
 * - User-friendly error messages
 */

import React from 'react';

export interface InputProps {
  id: string;
  name: string;
  type?: 'text' | 'email' | 'password' | 'textarea';
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  rows?: number;
  className?: string;
}

/**
 * Input Component
 *
 * @param id - Input ID (for label htmlFor)
 * @param name - Input name attribute
 * @param type - Input type (default: 'text')
 * @param label - Label text
 * @param value - Controlled value
 * @param onChange - Change handler
 * @param error - Error message (shows red border + message)
 * @param placeholder - Placeholder text
 * @param required - Required field indicator
 * @param disabled - Disabled state
 * @param rows - Textarea rows (if type='textarea')
 * @param className - Additional Tailwind classes
 */
export function Input({
  id,
  name,
  type = 'text',
  label,
  value,
  onChange,
  error,
  placeholder,
  required = false,
  disabled = false,
  rows = 4,
  className = '',
}: InputProps): React.JSX.Element {
  // Base classes for input/textarea
  const baseClasses =
    'appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none transition-colors duration-200 sm:text-sm';

  // Validation state classes
  const stateClasses = error
    ? 'border-red-400 text-red-900 focus:ring-red-500 focus:border-red-500'
    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500';

  // Disabled classes
  const disabledClasses = disabled ? 'bg-gray-100 cursor-not-allowed' : '';

  // Combine classes
  const inputClasses = `${baseClasses} ${stateClasses} ${disabledClasses} ${className}`;

  // Handle change for both input and textarea
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    onChange(e.target.value);
  };

  return (
    <div className="w-full">
      {/* Label */}
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      {/* Input or Textarea */}
      {type === 'textarea' ? (
        <textarea
          id={id}
          name={name}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          rows={rows}
          className={inputClasses}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? `${id}-error` : undefined}
        />
      ) : (
        <input
          id={id}
          name={name}
          type={type}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          autoComplete={
            type === 'email'
              ? 'email'
              : type === 'password'
              ? 'current-password'
              : undefined
          }
          className={inputClasses}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? `${id}-error` : undefined}
        />
      )}

      {/* Error Message */}
      {error && (
        <p
          id={`${id}-error`}
          className="mt-1 text-sm text-red-600"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  );
}
